import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CalendarPlus,
  UserPlus,
  Send,
  Calendar as CalendarIcon,
  UserCheck,
  StickyNote,
  Upload,
} from 'lucide-react';
import useResponsive from '../../hooks/useResponsive';
import { isRtl } from '../../i18n/direction';

/**
 * TherapyQuickActionsBar — the row of one-tap actions a therapy worker uses
 * day-to-day. On narrow screens the row becomes horizontally scrollable so
 * five-plus actions stay reachable with one thumb instead of wrapping into
 * three rows that push the rest of the dashboard below the fold.
 *
 * Stable data-testids are read by tests/therapy/12-quick-actions.spec.ts.
 */
export default function TherapyQuickActionsBar({
  onNewMeeting,
  onNewClient,
  onSendSurvey,
  onNewGuestMeeting,
  onAddNote,
  onUploadDocument,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rtl = isRtl(i18n.language);
  const { isMobile } = useResponsive();
  const scrollRef = useRef(null);

  const actions = [
    {
      testid: 'qa-new-meeting',
      icon: CalendarPlus,
      label: t('therapy.quickActions.newMeeting'),
      color: '#6366f1',
      bg: 'rgba(99, 102, 241, 0.1)',
      onClick: onNewMeeting,
    },
    {
      testid: 'qa-add-note',
      icon: StickyNote,
      label: t('therapy.quickActions.addNote'),
      color: '#0ea5e9',
      bg: 'rgba(14, 165, 233, 0.1)',
      onClick: onAddNote,
    },
    {
      testid: 'qa-new-client',
      icon: UserPlus,
      label: t('therapy.quickActions.newClient'),
      color: '#22c55e',
      bg: 'rgba(34, 197, 94, 0.1)',
      onClick: onNewClient,
    },
    {
      testid: 'qa-new-guest-meeting',
      icon: UserCheck,
      label: t('therapy.quickActions.newGuestMeeting'),
      color: '#14b8a6',
      bg: 'rgba(20, 184, 166, 0.1)',
      onClick: onNewGuestMeeting,
    },
    {
      testid: 'qa-send-survey',
      icon: Send,
      label: t('therapy.quickActions.shareSurvey'),
      color: '#8b5cf6',
      bg: 'rgba(139, 92, 246, 0.1)',
      onClick: onSendSurvey || (() => navigate('/surveys')),
    },
    {
      testid: 'qa-todays-calendar',
      icon: CalendarIcon,
      label: t('therapy.quickActions.todaysCalendar'),
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.1)',
      onClick: () => navigate('/calendar'),
    },
    // Mobile-only "Upload document" wizard. Hidden on tablet/desktop so
    // the bar doesn't grow on screens where the client detail page is
    // already one tap away.
    ...(isMobile && onUploadDocument ? [{
      testid: 'qa-upload-document',
      icon: Upload,
      label: t('dashboard.uploadDocument', 'Upload Document'),
      color: '#0ea5e9',
      bg: 'rgba(14, 165, 233, 0.1)',
      onClick: onUploadDocument,
    }] : []),
  ];

  return (
    <div
      data-testid="therapy-quick-actions-bar"
      style={{
        marginBottom: '1rem',
        direction: rtl ? 'rtl' : 'ltr',
      }}
    >
      <h3
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.5rem',
        }}
      >
        {t('therapy.quickActions.heading')}
      </h3>
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          gap: '0.5rem',
          // Wrap on tablet/desktop, horizontal-scroll on mobile so a thumb
          // can swipe through all the actions without 3-row wrapping that
          // would push today's meetings below the fold.
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          overflowX: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: isMobile ? 'x mandatory' : 'none',
          paddingBottom: isMobile ? '0.25rem' : 0,
          // hide scrollbar on mobile; keep keyboard scroll
          scrollbarWidth: 'none',
        }}
      >
        {actions.map(({ testid, icon: Icon, label, color, bg, onClick }) => (
          <button
            key={testid}
            data-testid={testid}
            onClick={onClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: isMobile ? '0.5rem 0.875rem' : '0.5rem 1rem',
              borderRadius: '9999px',
              border: 'none',
              backgroundColor: bg,
              color,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 500,
              minHeight: '44px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              scrollSnapAlign: 'start',
            }}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
